/**
 * 동일 처방 이미지 내 중복 의약품 확인 모달.
 * 레거시 `handleDuplicatedDrugsWithinImageModal` UI·병합 플로우 포팅.
 */
import type { PrescriptionImageDetailResponse } from '@/entities/prescription/api/prescription.api';
import {
  useCreateDrugPrescription,
  useDeletePrescriptionDrug,
} from '@/entities/prescription/hooks/usePrescriptions';
import {
  getPrescriptionRowKey,
  type StagingPrescriptionDrug,
} from '@/pages/prescriptions/prescriptionStagingUtils';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Separator } from '@/shared/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { handleApiError, useToast } from '@/shared/utils/toast';
import React, { useMemo } from 'react';

interface WithinImageDuplicateModalProps {
  open: boolean;
  onClose: () => void;
  duplicatedCodes: string[];
  drugs: StagingPrescriptionDrug[];
  imageDetail: PrescriptionImageDetailResponse;
  prescriptionImageCode: string;
  onMerged: () => void | Promise<void>;
}

/** 레거시 getDrugDataByCode — 코드별 중복 행·합계 */
export const getWithinImageDuplicateGroup = (
  code: string,
  drugs: StagingPrescriptionDrug[],
) => {
  const rows = drugs.filter((drug) => drug.drugCode === code);
  const first = rows[0];
  const totalInHospitalQuantity = rows.reduce(
    (sum, row) => sum + (row.inHospitalQuantity ?? 0),
    0,
  );
  const totalOutHospitalQuantity = rows.reduce(
    (sum, row) => sum + (row.outHospitalQuantity ?? 0),
    0,
  );
  return {
    drug: first,
    rows,
    totalInHospitalQuantity,
    totalOutHospitalQuantity,
  };
};

const formatQty = (value: number | null | undefined) =>
  Number(value ?? 0).toLocaleString();

const DrugInfoPanel: React.FC<{
  drug?: StagingPrescriptionDrug;
}> = ({ drug }) => (
  <dl className="overflow-hidden rounded-lg border border-border text-sm">
    {[
      { label: '의약품 명', value: drug?.drugName || '-' },
      { label: '제약사 명', value: drug?.pharmaCompanyName || '-' },
      { label: '의약품 코드', value: drug?.drugCode || '-' },
    ].map((item) => (
      <div
        key={item.label}
        className="grid grid-cols-[120px_1fr] border-b border-border last:border-b-0"
      >
        <dt className="bg-muted/50 px-4 py-2.5 font-semibold">{item.label}</dt>
        <dd className="px-4 py-2.5">{item.value}</dd>
      </div>
    ))}
  </dl>
);

export const WithinImageDuplicateModal: React.FC<
  WithinImageDuplicateModalProps
> = ({
  open,
  onClose,
  duplicatedCodes,
  drugs,
  imageDetail,
  prescriptionImageCode,
  onMerged,
}) => {
  const toast = useToast();
  const deleteDrug = useDeletePrescriptionDrug();
  const createDrug = useCreateDrugPrescription();
  const [merging, setMerging] = React.useState(false);

  const groups = useMemo(
    () =>
      duplicatedCodes.map((code) => ({
        code,
        ...getWithinImageDuplicateGroup(code, drugs),
      })),
    [duplicatedCodes, drugs],
  );

  /** 레거시: 삭제 후 합산 수량으로 단건 POST → 재조회 → cross-image/확정 */
  const handleMergeAndContinue = async () => {
    setMerging(true);
    try {
      for (const group of groups) {
        const { drug, rows, totalInHospitalQuantity, totalOutHospitalQuantity } =
          group;
        if (!drug || rows.length === 0) continue;

        const prescriptionMrIds = rows
          .map((row) => row.prescriptionMrId)
          .filter((mrId): mrId is number => mrId != null);

        for (const prescriptionMrId of prescriptionMrIds) {
          await deleteDrug.mutateAsync(prescriptionMrId);
        }

        await createDrug.mutateAsync({
          prescriptionImageCode,
          userId: imageDetail.userId,
          clinicId: imageDetail.clinicId,
          drugCode: drug.drugCode,
          pharmaCompanyId: drug.pharmaCompanyId ?? 0,
          prescriptionYearMonth: imageDetail.prescriptionYearMonth,
          prescriptionRegisteredYearMonth:
            imageDetail.prescriptionRegisteredYearMonth,
          settlementYearMonth: imageDetail.settlementYearMonth,
          price: drug.price ?? 0,
          outHospitalQuantity: totalOutHospitalQuantity,
          inHospitalQuantity: totalInHospitalQuantity,
        });
      }

      toast.success('단일 내역으로 확정되었습니다.');
      onClose();
      await onMerged();
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[min(96vw,1100px)] max-w-[80vw] flex-col gap-6 overflow-hidden">
        <DialogHeader className="shrink-0 text-center sm:text-center">
          <DialogTitle className="text-2xl">동일 처방 의약품 확인</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-10 overflow-y-auto pr-1">
          {groups.map((group, codeIndex) => (
            <div key={group.code} className="space-y-5">
              {codeIndex > 0 ? <Separator /> : null}
              <h3 className="text-lg font-semibold">
                동일 처방 의약품 {codeIndex + 1}
              </h3>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <DrugInfoPanel drug={group.drug} />
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/80 hover:bg-muted/80">
                      <TableHead className="w-16 text-center">내역</TableHead>
                      <TableHead className="text-center">원내 처방량</TableHead>
                      <TableHead className="text-center">원외 처방량</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.rows.map((row, index) => (
                      <TableRow key={getPrescriptionRowKey(row) || index}>
                        <TableCell className="text-center">
                          {index + 1}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-block min-w-[4rem] rounded border border-input bg-background px-2 py-0.5 text-right text-xs tabular-nums">
                            {formatQty(row.inHospitalQuantity)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-block min-w-[4rem] rounded border border-input bg-background px-2 py-0.5 text-right text-xs tabular-nums">
                            {formatQty(row.outHospitalQuantity)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {/* 레거시 Table.Summary — 어두운 합계 행 */}
                  <TableFooter>
                    <TableRow className="border-0 bg-[#5e5e5e] text-white hover:bg-[#5e5e5e]">
                      <TableCell className="text-center font-semibold text-white">
                        합계
                      </TableCell>
                      <TableCell className="text-center font-semibold text-white tabular-nums">
                        {formatQty(group.totalInHospitalQuantity)}
                      </TableCell>
                      <TableCell className="text-center font-semibold text-white tabular-nums">
                        {formatQty(group.totalOutHospitalQuantity)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="shrink-0 justify-center gap-4 sm:justify-center">
          <Button
            variant="outline"
            className="ruda-button-outline min-w-28"
            onClick={onClose}
            disabled={merging}
          >
            취소
          </Button>
          <Button
            className="ruda-button min-w-40"
            onClick={() => void handleMergeAndContinue()}
            disabled={merging}
          >
            단일 내역으로 확정
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
